import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from '@maximizer/core/shared/data-access';
import {
  EntryType,
  Octopus,
  Timeline,
  TimelineConfiguration,
  TimelineEvent,
} from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';
import { TimelineFilter, TimelineKeys } from '../state/timeline.models';
import { BaseTimelineService } from './base-timeline-service';
import { isObject } from '@maximizer/shared/util';

@Injectable()
export class TimelineService extends BaseTimelineService {
  readonly fields: Octopus.Scope<Octopus.Timeline> = {
    Key: 1,
    Type: {
      Icon: 1,
      Key: 1,
      DisplayValue: 1,
      External: 1,
    },
    Source: 1,
    Subject: 1,
    Result: {
      DisplayValue: 1,
    },
    Description: 1,
    Users: [
      {
        Key: {
          Uid: 1,
          Value: 1,
        },
        DisplayValue: 1,
      },
    ],
    AbEntries: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    Leads: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    NoteType: {
      Value: 1,
      DisplayValue: 1,
    },
    DocumentType: {
      Value: 1,
      DisplayValue: 1,
    },
    Category: {
      Value: 1,
      DisplayValue: 1,
    },
    StartDate: {
      Value: 1,
      DisplayValue: 1,
    },
    EndDate: {
      Value: 1,
      DisplayValue: 1,
    },
    Priority: {
      Value: 1,
      DisplayValue: 1,
    },
    Creator: {
      Value: 1,
      DisplayValue: 1,
    },
    With: {
      Opportunity: {
        Key: 1,
        Objective: 1,
      },
      Case: {
        Key: 1,
        CaseNumber: 1,
        Subject: 1,
      },
    },
    Attachment: 1,
    FileNames: 1,
    Completed: {
      Value: 1,
    },
    Direction: 1,
    Recurring: 1,
    SecStatus: {
      CanRead: 1,
      CanDelete: 1,
      CanUpdate: 1,
    },
  };

  constructor(
    protected http: HttpClient,
    protected context: ContextService,
  ) {
    super();
  }

  forUser(filter: TimelineFilter): Observable<Timeline | null> {
    const request: Octopus.TimelineReadRequest = {
      Timeline: {
        Scope: { Fields: this.fields },
        Criteria: this.getCriteria(filter),
        OrderBy: {
          Fields: {
            StartDate: 'ASC',
          },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.http
      .post<Octopus.TimelineResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Timeline?.Data
          ) {
            const mapper = new Octopus.TimelineMapper();
            return mapper.from(result.Timeline.Data, filter.referenceDate);
          }
          return null;
        }),
      );
  }

  forEntry(
    key: string | TimelineKeys,
    type: EntryType,
    filter: TimelineFilter,
    top?: number,
    orderByFields?: Octopus.OrderBy<
      Octopus.Timeline | Octopus.OrderBy<Octopus.Timeline>[]
    >,
  ): Observable<TimelineEvent[]> {
    const criteria = this.getCriteriaForKeys<Octopus.Timeline>(
      key,
      type,
      filter,
    );
    const request: Octopus.TimelineReadRequest = {
      Timeline: {
        Scope: { Fields: this.fields },
        Criteria: {
          ...criteria,
          Top: top,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    if (orderByFields) {
      request.Timeline.OrderBy = {
        Fields: orderByFields,
      };
    }

    return this.http
      .post<Octopus.TimelineResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Timeline?.Data
          ) {
            const mapper = new Octopus.TimelineMapper();
            return mapper.getEvents(result.Timeline.Data);
          }
          return [];
        }),
      );
  }

  forEvent(key: string): Observable<TimelineEvent | null> {
    const request: Octopus.TimelineReadRequest = {
      Timeline: {
        Scope: { Fields: this.fields },
        Criteria: {
          SearchQuery: {
            Key: { $EQ: key },
          },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.http
      .post<Octopus.TimelineResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Timeline?.Data?.length
          ) {
            const mapper = new Octopus.TimelineMapper();
            return mapper.mapEvent(result.Timeline.Data[0]);
          }
          return null;
        }),
      );
  }

  getConfiguration(): Observable<TimelineConfiguration | null> {
    const request: Octopus.TimelineFieldOptionsRequest = {
      Timeline: {
        FieldOptions: {
          Type: [
            {
              Key: 1,
              Icon: 1,
              DisplayValue: 1,
            },
          ],
          NoteType: [
            {
              Value: 1,
              DisplayValue: 1,
            },
          ],
          DocumentType: [
            {
              Value: 1,
              DisplayValue: 1,
            },
          ],
        },
      },
      InteractionLog: {
        FieldOptions: {
          Category: {
            Scope: {
              Fields: {
                Value: 1,
                Name: 1,
              },
            },
            Criteria: {
              SearchQuery: {
                InActive: {
                  $EQ: false,
                },
              },
            },
            OrderBy: {
              Fields: [
                {
                  Name: 'ASC',
                },
              ],
            },
          },
        },
      },
    };

    return this.http
      .post<Octopus.TimelineFieldOptionsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Timeline &&
            result.InteractionLog
          ) {
            const mapper = new Octopus.TimelineConfigurationMapper();
            return mapper.from(result);
          }
          return null;
        }),
      );
  }

  getCriteriaForKeys<T>(
    key: string | TimelineKeys,
    type: EntryType,
    filter: TimelineFilter,
  ): Octopus.Criteria<T> {
    let criteria: Octopus.Criteria<T>;

    if (this.isOpportunityOrCaseModule(type) && isObject(key)) {
      const createCriteria = (
        key?: string | null,
        type?: EntryType,
        additionalFilter?: { showAll: boolean },
        ignoreKey = false,
      ) =>
        key || ignoreKey
          ? this.getCriteria(
              { ...filter, ...additionalFilter },
              key,
              type,
              false,
              ignoreKey,
            ).SearchQuery
          : {};
      const timelineKeys = key as TimelineKeys;
      const unionCriterias = [];

      unionCriterias.push(
        createCriteria(
          timelineKeys.abEntryKey ?? null,
          'addressBook',
          { showAll: true },
          !timelineKeys.ignoreAbEntry,
        ),
      );

      if (timelineKeys.opportunityKey) {
        unionCriterias.push(
          createCriteria(timelineKeys.opportunityKey, 'opportunity'),
        );

        if (timelineKeys.leadKey) {
          unionCriterias.push(createCriteria(timelineKeys.leadKey, 'lead'));
        }
      } else if (timelineKeys.caseKey) {
        unionCriterias.push(createCriteria(timelineKeys.caseKey, 'case'));
      }

      criteria = {
        SearchQuery: {
          $UNION: unionCriterias,
        },
      };
    } else {
      criteria = this.getCriteria(filter, key as string, type);
    }
    return criteria;
  }

  private isOpportunityOrCaseModule(entryType: EntryType): boolean {
    return entryType === 'opportunity' || entryType === 'case';
  }
}
